import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../services/api/client";
import Icon from "../../components/AppIcon";

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatHours = (h) => {
  if (h == null) return "-";
  return `${Number(h).toFixed(1)}h`;
};

const toISODate = (d) => d.toISOString().split("T")[0];

const MyClockRecords = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date range - default last 30 days
  const [datePreset, setDatePreset] = useState("30d");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toISODate(d);
  });
  const [dateTo, setDateTo] = useState(() => toISODate(new Date()));

  const handleDatePreset = useCallback((preset) => {
    setDatePreset(preset);
    const today = new Date();
    const to = toISODate(today);
    let from;
    switch (preset) {
      case "7d":
        from = new Date(today);
        from.setDate(from.getDate() - 7);
        setDateFrom(toISODate(from));
        setDateTo(to);
        break;
      case "30d":
        from = new Date(today);
        from.setDate(from.getDate() - 30);
        setDateFrom(toISODate(from));
        setDateTo(to);
        break;
      default:
        break;
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // First, find the employee record for the current user
      const meResponse = await apiClient.get("/employees/me/tronc-report", {
        fromDate: new Date(dateFrom).toISOString(),
        toDate: new Date(dateTo + "T23:59:59").toISOString(),
      });

      const employeeId = meResponse?.data?.employeeId;
      if (!employeeId) {
        // No employee record found - user might not be linked to an employee
        setRecords([]);
        setLoading(false);
        return;
      }

      // Fetch clock records for this employee
      const response = await apiClient.get("/clockrecords", {
        employeeId,
        fromDate: new Date(dateFrom).toISOString(),
        toDate: new Date(dateTo + "T23:59:59").toISOString(),
      });

      if (response?.success) {
        setRecords(response.data || []);
      } else {
        setRecords([]);
      }
    } catch (err) {
      console.error("Failed to fetch clock records:", err);
      setError(err.message || "Failed to load your clock records");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Stats
  const stats = useMemo(() => {
    const totalHours = records.reduce(
      (sum, r) => sum + (r.hoursWorked || 0),
      0,
    );
    const completedShifts = records.filter(
      (r) => r.status === "Completed",
    ).length;
    const inProgressShifts = records.filter(
      (r) => r.status === "InProgress",
    ).length;
    return {
      totalHours,
      completedShifts,
      inProgressShifts,
      totalRecords: records.length,
    };
  }, [records]);

  // Export
  const handleExport = () => {
    if (records.length === 0) return;
    const headers = [
      "Date",
      "Clock In",
      "Clock Out",
      "Hours Worked",
      "Location",
      "Status",
    ];
    const rows = records.map((r) => [
      formatDate(r.clockIn),
      formatTime(r.clockIn),
      r.clockOut ? formatTime(r.clockOut) : "In Progress",
      r.hoursWorked != null ? Number(r.hoursWorked).toFixed(2) : "",
      r.location || "-",
      r.status || "-",
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `my-clock-records-${dateFrom}-${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>My Clock Records - Rocket Tronc</title>
        </Helmet>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon
              name="Loader2"
              size={48}
              className="animate-spin text-primary mx-auto mb-4"
            />
            <p className="text-muted-foreground">
              Loading your clock records...
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Clock Records - Rocket Tronc</title>
      </Helmet>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">My Clock Records</h1>
            <p className="text-muted-foreground">
              View your shift clock-in/out history
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="btn btn-outline btn-sm"
              disabled={records.length === 0}
            >
              <Icon name="Download" size={16} />
              Export
            </button>
          </div>
        </div>

        {error && (
          <div className="card p-8 text-center">
            <Icon
              name="AlertTriangle"
              size={48}
              className="text-warning mx-auto mb-4"
            />
            <h3 className="font-semibold mb-2">Unable to Load Records</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button onClick={fetchRecords} className="btn btn-primary">
              <Icon name="RefreshCw" size={18} />
              Try Again
            </button>
          </div>
        )}

        {!error && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="Clock" size={20} className="text-primary" />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Total Hours
                  </span>
                </div>
                <p className="text-3xl font-bold font-mono">
                  {stats.totalHours.toFixed(1)}h
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {stats.totalRecords} shift
                  {stats.totalRecords !== 1 ? "s" : ""}
                </p>
              </div>

              <div className="card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <Icon
                      name="CheckCircle"
                      size={20}
                      className="text-success"
                    />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Completed Shifts
                  </span>
                </div>
                <p className="text-3xl font-bold font-mono">
                  {stats.completedShifts}
                </p>
              </div>

              <div className="card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                    <Icon name="Timer" size={20} className="text-warning" />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    In Progress
                  </span>
                </div>
                <p className="text-3xl font-bold font-mono">
                  {stats.inProgressShifts}
                </p>
              </div>
            </div>

            {/* Date Filter */}
            <div className="card">
              <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                  {[
                    { key: "7d", label: "Last 7 days" },
                    { key: "30d", label: "Last 30 days" },
                    { key: "custom", label: "Custom" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => handleDatePreset(opt.key)}
                      className={`px-3 py-1.5 border rounded-md text-sm font-medium transition-all ${
                        datePreset === opt.key
                          ? "bg-white text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <Icon
                    name="Calendar"
                    size={16}
                    className="text-muted-foreground"
                  />
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDatePreset("custom");
                      setDateFrom(e.target.value);
                    }}
                    className="input input-sm w-36"
                  />
                  <span className="text-muted-foreground text-sm">to</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDatePreset("custom");
                      setDateTo(e.target.value);
                    }}
                    className="input input-sm w-36"
                  />
                </div>

                <button
                  onClick={fetchRecords}
                  className="btn btn-outline btn-sm sm:ml-auto"
                  disabled={loading}
                >
                  <Icon
                    name="RefreshCw"
                    size={16}
                    className={loading ? "animate-spin" : ""}
                  />
                </button>
              </div>
            </div>

            {/* Records Table */}
            {records.length === 0 ? (
              <div className="card p-8 text-center">
                <Icon
                  name="Clock"
                  size={48}
                  className="text-muted-foreground mx-auto mb-4"
                />
                <h3 className="font-semibold mb-2">No Clock Records</h3>
                <p className="text-muted-foreground">
                  You don't have any clock records for this period.
                </p>
              </div>
            ) : (
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                          Clock In
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                          Clock Out
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                          Hours
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                          Location
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-muted-foreground">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r) => (
                        <tr
                          key={r.id}
                          className="border-b border-border hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-4 py-3 font-medium">
                            {formatDate(r.clockIn)}
                          </td>
                          <td className="px-4 py-3">{formatTime(r.clockIn)}</td>
                          <td className="px-4 py-3">
                            {r.clockOut ? (
                              formatTime(r.clockOut)
                            ) : (
                              <span className="text-warning">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-medium font-mono">
                            {formatHours(r.hoursWorked)}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {r.location || "-"}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`badge text-xs ${
                                r.status === "Completed"
                                  ? "badge-success"
                                  : r.status === "InProgress"
                                    ? "badge-warning"
                                    : "badge-outline"
                              }`}
                            >
                              {r.status === "InProgress"
                                ? "In Progress"
                                : r.status || "-"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/30 font-semibold">
                      <tr>
                        <td className="px-4 py-3" colSpan="3">
                          Total
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {stats.totalHours.toFixed(1)}h
                        </td>
                        <td className="px-4 py-3" colSpan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Help Section */}
            <div className="card p-6 bg-muted/30">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon name="HelpCircle" size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">About Clock Records</h4>
                  <p className="text-sm text-muted-foreground">
                    Your clock records are synced from the timekeeping system
                    and used for TRONC distribution calculations. If you notice
                    any discrepancies, please contact your manager.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default MyClockRecords;
